import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchVariableTypes,
  updateVariableType,
  createVariableType,
  deleteVariableType,
} from "../../api/service/settings";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
};

export const variableTypesController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteVariableTypeAsync = createAsyncThunk(
  "agreement_types/deleteVariableTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteVariableType(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_variable_type");
    }
  }
);

export const createVariableTypeAsync = createAsyncThunk(
  "agreement_types/createVariableTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createVariableType(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "create_variable_type"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_variable_type");
    }
  }
);

export const updateVariableTypeAsync = createAsyncThunk(
  "agreement_types/updateVariableTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateVariableType(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "update_variable_type"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_variable_type");
    }
  }
);

export const getVariableTypesAsync = createAsyncThunk(
  "agreement_types/getVariableTypesAsync",
  async (controller = variableTypesController, { rejectWithValue }) => {
    try {
      const response = await fetchVariableTypes(controller);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "list_variable_types"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_variable_types");
    }
  }
);

const variableTypeSlice = createSlice({
  name: "variable_types",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getVariableTypesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getVariableTypesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(getVariableTypesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente variabeltypene."
        );
      })
      .addCase(updateVariableTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateVariableTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.payload?.id;
          const index = draftState.data.findIndex((type) => type.id === id);
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateVariableTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere variabeltypen."
        );
      })
      .addCase(createVariableTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createVariableTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createVariableTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette variabeltypen."
        );
      })
      .addCase(deleteVariableTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteVariableTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteVariableTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette variabeltypen."
        );
      });
  },
});

export const { reducer } = variableTypeSlice;

export default variableTypeSlice;

export const selectVariableTypeById = (state, TypeId) =>
  state.variable_types.data.find((type) => type.id == TypeId);
